import {Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {LayerUtilService} from './layer-util.service';
import {MapToolsService} from '../services/map-tools.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {equals} from '../../common/service/data-util';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class AnnotationLayerService {
    private readonly layerId = 'annotationFeatureLayer_layer';

    private readonly layerSelector = `#${this.layerId}`;

    private readonly featureClass = 'annotation';

    private readonly featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);

    private lastScaleFactor = 0;

    private svgStyle: string;

    private trustedSvgStyle: SafeHtml;

    private setCircleTransformsTimeout: number;

    private setCircleTransformsTimeoutFeatureIds = [];

    private mouseDownInfo?: {
        x: number;
        y: number;
        graphic: any;
        pristineGraphic: string;
        graphicX: number;
        graphicY: number;
        bearing: number;
    };

    constructor(
        private domSanitizer: DomSanitizer,
        private layerUtilService: LayerUtilService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private spatialUtilService: SpatialUtilService
    ) {}

    getSvgStyle(): SafeHtml {
        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return '';
        }

        const backgroundColor = this.layerUtilService.showFeatures()
            ? this.spatialUtilService.getMapBackground() === 'white'
                ? 'black'
                : 'white'
            : 'transparent';

        const mouseOverUseElementSelector = `use[href^="#annotation-feature-"]`;

        const styles = [
            `${this.layerSelector} circle[${this.featureIdAttrName}][data-selected]` +
                ` + g[${this.featureIdAttrName}] > text > tspan {fill:${this.layerUtilService.getSelectedColor()};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}][data-hidden]:not([data-selected])` +
                ` + g[${this.featureIdAttrName}] > text > tspan {fill:${this.layerUtilService.getHiddenColor()};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}]` +
                ` + g[${this.featureIdAttrName}] > text {font-family: Arial; font-size: ${scaleFactor}px; fill: ${backgroundColor};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}]:hover` +
                ` + g[${this.featureIdAttrName}] {animation: feature-symbol-hover 0.3s alternate ease-in-out none infinite;}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}]:not([data-hidden]):not([data-selected]):hover` +
                ` + g[${this.featureIdAttrName}] > text > tspan {fill:${this.layerUtilService.getHoverColor()};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}][data-hidden]:not([data-selected]):hover` +
                ` + g[${this.featureIdAttrName}] > text > tspan {fill:${this.layerUtilService.getHoverColor()};}`,

            mouseOverUseElementSelector +
                ` {font-family: Arial; font-size: ${scaleFactor}px; fill:${this.layerUtilService.getHoverColor()}; animation: feature-symbol-hover 0.3s alternate ease-in-out none infinite;}`,
        ];

        if (this.mapToolsService.isScaleActive()) {
            styles.push(
                `${this.layerSelector} circle[${this.featureIdAttrName}]${
                    this.mapToolsService.isMoveActive() || this.mapToolsService.isRotateActive() ? '' : '[data-selected]'
                }:hover {cursor:pointer;}`
            );
        }

        const newSvgStyle = styles.join('\n');
        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }

        this.lastScaleFactor = scaleFactor;

        this.svgStyle = newSvgStyle;

        this.trustedSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    init() {
        this.setCircleTransformsTimeout = null;
        this.setCircleTransformsTimeoutFeatureIds.length = 0;

        const annotationFeatureLayer = this.spatialUtilService.getAnnotationMapLayer();

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'graphic-node-add', (evt: any) => {
            const featureId = evt.graphic.attributes.id;

            this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

            this.removeNodes(featureId);

            this.layerUtilService.setAnnotationLabelProperties(evt.graphic);

            if (evt.graphic.attributes.labelproperties) {
                // hide the node
                evt.node.setAttribute('style', 'fill-opacity:0;stroke-opacity:0');

                evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);

                const labels = JSON.parse(evt.graphic.attributes.labelproperties);
                this.createTextLabels(evt, labels);
            }
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'graphic-node-remove', (evt: any) => {
            const featureId = evt.graphic.attributes.id;

            this.removeNodes(featureId);
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'graphics-clear', () => {
            // console.info("graphics-clear");
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'edits-complete', () => {
            const graphicIds = this.spatialUtilService
                .getGraphics(this.featureClass)
                .map((g: any) => g.attributes.id)
                .sort((a: number, b: number) => a - b);

            const layerElt = document.getElementById(this.layerId);

            const groupNodeFeatureIds = Array.from(layerElt.querySelectorAll(`g[${this.featureIdAttrName}]`)).map(v =>
                parseInt(v.getAttribute(this.featureIdAttrName))
            );

            const circleNodeFeatureIds = Array.from(layerElt.querySelectorAll(`circle[${this.featureIdAttrName}]`)).map(v =>
                parseInt(v.getAttribute(this.featureIdAttrName))
            );

            const nodeFeatureIds = [...groupNodeFeatureIds, ...circleNodeFeatureIds].filter((v, i, a) => i === a.indexOf(v)).sort((a, b) => a - b);

            const {nodeFeatureIdsToKeep, nodeFeatureIdsToRemove} = nodeFeatureIds.reduce(
                (acc, v) => {
                    if (graphicIds.includes(v)) {
                        acc.nodeFeatureIdsToKeep.push(v);
                    } else {
                        acc.nodeFeatureIdsToRemove.push(v);
                    }
                    return acc;
                },
                {nodeFeatureIdsToKeep: [], nodeFeatureIdsToRemove: []}
            );

            nodeFeatureIdsToRemove.forEach(v => this.removeNodes(v));

            this.setCircleTransforms(nodeFeatureIdsToKeep);
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'graphic-draw', (evt: {graphic: any; node: any}) => {
            const graphic = evt.graphic;
            const featureId = graphic.attributes.id;

            this.layerUtilService.setHiddenAttr(evt.node, this.featureClass, featureId);

            const {cx, cy} = this.layerUtilService.getGraphicShape(graphic);

            // hide the label node
            evt.node.setAttribute('style', 'fill-opacity:0;stroke-opacity:0');

            const g = evt.node.nextSibling;
            if (g) {
                const pointOffset = this.layerUtilService.getPointOffset(cx, cy, undefined, graphic, undefined);
                const x = pointOffset.x || 0;
                const y = pointOffset.y || 0;

                g.setAttribute('transform', `translate(${x}, ${y}) rotate(${this.layerUtilService.getSvgRotation(graphic.attributes.rotation)})`);

                this.setCircleTransform(featureId, pointOffset);

                this.layerUtilService.setGraphicExtentToDisplayNodeExtent(this.featureClass, graphic);
            }
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'click', (evt: any) => {
            this.layerUtilService.onClick(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'dbl-click', (evt: any) => {
            this.layerUtilService.setSelectedAttr(evt.target, this.featureClass, evt.graphic.attributes.id);
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'mouse-over', (evt: any) => {
            this.layerUtilService.onMouseOver(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'mouse-out', (evt: any) => {
            this.layerUtilService.onMouseOut(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(annotationFeatureLayer, 'mouse-down', (evt: any) => {
            if (
                evt.which !== 1 ||
                evt.altKey ||
                evt.ctrlKey ||
                evt.shiftKey ||
                !this.mapToolsService.isScaleActive() ||
                this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') ||
                !this.permissionsService.hasEditPermission(['EDIT_DESIGN'])
            ) {
                return;
            }

            const {x, y} = evt.mapPoint;
            const graphic = evt.graphic;

            this.layerUtilService.suspendDragDrawTools(this.featureClass, graphic.attributes.id);

            this.mouseDownInfo = {
                x: x,
                y: y,
                graphic: graphic,
                pristineGraphic: evt.graphic.toJson(),
                graphicX: graphic.geometry.x,
                graphicY: graphic.geometry.y,
                bearing: this.layerUtilService.calcBearing(x, y, graphic.geometry.x, graphic.geometry.y) - (graphic.attributes.rotation || 0)
            };
        });

        const map = this.spatialUtilService.getMap();

        map.on('mouse-drag', (evt: {mapPoint: any}) => {
            if (!this.mouseDownInfo) {
                return;
            }

            const graphic = this.mouseDownInfo.graphic;

            if (
                this.mapToolsService.isMoveActive() ||
                (!this.mapToolsService.isRotateActive() && this.spatialUtilService.isHighlightedFeature(this.featureClass, graphic.attributes.id))
            ) {
                graphic.geometry.x = evt.mapPoint.x + this.mouseDownInfo.graphicX - this.mouseDownInfo.x;
                graphic.geometry.y = evt.mapPoint.y + this.mouseDownInfo.graphicY - this.mouseDownInfo.y;

                graphic.draw();

                // NLRP19-1585 - Rotate feature will not allow user to move the annotation after selecting it.
            } else if (
                this.mapToolsService.isRotateActive() &&
                !this.spatialUtilService.isHighlightedFeature(this.featureClass, graphic.attributes.id)
            ) {
                const bearing = this.layerUtilService.calcBearing(evt.mapPoint.x, evt.mapPoint.y, graphic.geometry.x, graphic.geometry.y);
                const rotation = (Math.round(bearing - this.mouseDownInfo.bearing) + 360) % 360;

                graphic.attributes.rotation = rotation;
                graphic.attributes.rotationdms = `${rotation}`;

                graphic.draw();
            } else if (
                this.mapToolsService.isRotateActive() &&
                this.spatialUtilService.isHighlightedFeature(this.featureClass, graphic.attributes.id)
            ) {
                graphic.geometry.x = evt.mapPoint.x + this.mouseDownInfo.graphicX - this.mouseDownInfo.x;
                graphic.geometry.y = evt.mapPoint.y + this.mouseDownInfo.graphicY - this.mouseDownInfo.y;
                graphic.draw();
            }
        });

        map.on('mouse-out', () => this.onMouseDragEnd());

        map.on('mouse-up', () => this.onMouseDragEnd());

        return annotationFeatureLayer;
    }

    private onMouseDragEnd() {
        if (this.mouseDownInfo) {
            if (!equals(this.mouseDownInfo.pristineGraphic, this.mouseDownInfo.graphic.toJson())) {
                this.spatialUtilService.applyEdits(this.featureClass, null, [this.mouseDownInfo.graphic], null).subscribe();
            }

            this.layerUtilService.restoreDragDrawTools();
            this.mouseDownInfo = null;
        }
    }

    private createTextLabels(evt: {graphic: any; node: any}, labels: any[]) {
        const featureId = evt.graphic.attributes.id;

        evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);
        this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('id', this.layerUtilService.getFeatureNodeId(this.featureClass, featureId));
        g.setAttribute(this.featureIdAttrName, `${featureId}`);
        g.setAttribute('pointer-events', 'none');

        const {cx, cy} = this.layerUtilService.getGraphicShape(evt.graphic);

        const pointOffset = this.layerUtilService.getPointOffset(cx, cy, undefined, evt.graphic, undefined);
        const x = pointOffset.x || 0;
        const y = pointOffset.y || 0;

        g.setAttribute('transform', `translate(${x}, ${y}) rotate(${this.layerUtilService.getSvgRotation(evt.graphic.attributes.rotation)})`);

        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');

        const annotations = labels;
        let annotationLen = 0;
        if (annotations.length === 1) {
            annotationLen = annotations.length;
            // this may be better to be text-after-edge when annotation is not for line or angle
            text.style.dominantBaseline = 'middle';

            const showFeatures = this.layerUtilService.showFeatures();

            annotations.map(l => {
                if (!Array.isArray(l)) {
                    const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');

                    tspan.textContent = l.value;
                    tspan.setAttribute('font-size', `${l.style.fontSize}`);
                    tspan.setAttribute('font-style', `${l.style.fontStyle}`);
                    tspan.setAttribute('fill', showFeatures ? `${l.style.fill}` : 'transparent');

                    tspan.setAttribute('x', '0');
                    tspan.setAttribute('y', '0');
                    l.style.textAnchor === 'start' ? tspan.setAttribute('dx', '0.15em') : tspan.setAttribute('dx', '0.0em');

                    text.appendChild(tspan);
                    text.style.textAnchor = l.style.textAnchor;
                } else {
                    // if this is an array of different styles, i.e.
                    // {style: {"fontSize": '10em', "fontStyle": 'normal', "textAnchor": 'middle'}, value: '('},
                    // {style: {"fontSize": '10em', "fontStyle": 'italic', "textAnchor": 'middle'}, value: '598ha'},
                    // {style: {"fontSize": '10em', "fontStyle": 'normal', "textAnchor": 'middle'}, value: ')'}
                    if (l.length > 0) {
                        const singleLineProperties = this.getSingleLineText(l).lineProperties;
                        this.setSingleLineTspan(singleLineProperties, text);
                    }
                }

                this.setMultiLineDy(text, annotationLen);
            });
        }

        if (annotations.length > 1) {
            text.style.dominantBaseline = 'text-after-edge';
            text.style.textAnchor = annotations[0].style.textAnchor;

            annotations.map((l, i) => {
                if (!Array.isArray(l)) {
                    const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');

                    this.setNewLine(tspan, i);

                    tspan.textContent = l.value;
                    tspan.setAttribute('font-size', `${l.style.fontSize}`);
                    tspan.setAttribute('font-style', `${l.style.fontStyle}`);

                    text.style.textAnchor === 'start' ? tspan.setAttribute('dx', '0.15em') : tspan.setAttribute('dx', '0.0em');

                    text.appendChild(tspan);

                    annotationLen++;
                } else {
                    // if this is an array of different styles, i.e.
                    // {style: {"fontSize": '10em', "fontStyle": 'normal', "textAnchor": 'middle'}, value: '('},
                    // {style: {"fontSize": '10em', "fontStyle": 'italic', "textAnchor": 'middle'}, value: '598ha'},
                    // {style: {"fontSize": '10em', "fontStyle": 'normal', "textAnchor": 'middle'}, value: ')'}
                    if (l.length > 0) {
                        const singleLineProperties = this.getSingleLineText(l).lineProperties;
                        this.setSingleLineTspan(singleLineProperties, text);
                        annotationLen++;
                    }
                }
            });

            this.setMultiLineDy(text, annotationLen);
        }

        g.appendChild(text);

        document.getElementById(this.layerId).insertBefore(g, evt.node.nextSibling);

        this.setCircleTransform(featureId, undefined);
    }

    private setMultiLineDy(text: any, lineCount: number) {
        switch (lineCount) {
            case 1:
                text.setAttribute('dy', '1.01em');
                break;
            case 2:
                text.setAttribute('dy', this.calculateMultilineDy(text, 4.25, 2.3, -0.05) + 'em');
                break;
            case 3:
                text.setAttribute('dy', this.calculateMultilineDy(text, -1.6, -5.5, -4.2) + 'em');
                break;
            case 4:
                text.setAttribute('dy', this.calculateMultilineDy(text, -6.8, -6.0, -8.4) + 'em');
                break;
            case 5:
                text.setAttribute('dy', this.calculateMultilineDy(text, -12.0, -14.6, -12.5) + 'em');
                break;
            case 6:
                text.setAttribute('dy', '-16.4em');
                break;
            case 7:
                text.setAttribute('dy', '-20.8em');
                break;
            case 8:
                text.setAttribute('dy', '-24.8em');
                break;
            case 9:
                text.setAttribute('dy', '-28.8em');
                break;
            case 10:
                text.setAttribute('dy', '-32.8em');
                break;
            default:
                text.setAttribute('dy', '1.01em');
        }
    }

    private getFontSizeListFromText(text: any) {
        const fontSizeList = [];

        text.childNodes.forEach((tspan: any, i: number) => {
            if (i === 0 || tspan.getAttribute('dy')) {
                fontSizeList.push({
                    index: i,
                    fontSize: tspan.getAttribute('font-size')
                });
            }
        });

        return fontSizeList;
    }

    private calculateMultilineDy(text: any, font14Offset: number, font10Offset: number, fontOffset: number) {
        // obtain a list of font sizes of <trans> as the child nodes of <text>
        const fontSizeList = this.getFontSizeListFromText(text);
        let dyOffset = fontOffset;
        // calculate a dy value of <text> of the group of lines
        if (fontSizeList[0].fontSize === '14em') {
            dyOffset = font14Offset - fontSizeList.filter(f => f.fontSize === '14em').length * 2.666;
        }
        if (fontSizeList[0].fontSize === '10em') {
            dyOffset = font10Offset - fontSizeList.filter(f => f.fontSize === '10em').length * 1.333;
        }

        return dyOffset.toFixed(2);
    }

    private setNewLine(tspan: any, i: number) {
        tspan.setAttribute('x', '0');

        // if the first line set the y attribute
        i === 0 && tspan.setAttribute('y', '0');

        // set the new line if this is not the first line
        i && tspan.setAttribute('dy', '1.15em');
    }

    private setSingleLineTspan(lineProperties: any, text: any) {
        //Modify the existing tspans
        const children = text.childNodes;
        for (let i = 0; i < children.length; i++) {
            children[i].setAttribute('x', '0');
            i === 1 && children[i].setAttribute('dy', '1.15em');
        }

        // create the single line tspans
        lineProperties.words.forEach((w: any, i: number) => {
            text.style.textAnchor = w.style.textAnchor;

            const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');

            if (i === 0) {
                if (w.value === '(') {
                    tspan.setAttribute('dy', '1.15em');
                } else {
                    tspan.setAttribute('dy', '0');
                }
                tspan.setAttribute('x', '0');
            }

            tspan.textContent = w.value;
            tspan.setAttribute('font-size', `${w.style.fontSize}`);
            tspan.setAttribute('font-style', `${w.style.fontStyle}`);
            Object.assign(tspan.style, w.style);

            text.appendChild(tspan);
        });
    }

    private getSingleLineText(singleLineLabels: any[]) {
        let textLength = 0;

        const words = singleLineLabels.map((v, i) => {
            textLength += v.value.length;
            return {
                value: v.value,
                position: i,
                characterCount: v.value.length,
                style: v.style
            };
        });

        return {
            lineProperties: {
                numberOfWords: singleLineLabels.length,
                words: words,
                totalCharacterCount: textLength
            }
        };
    }

    private removeNodes(featureId: number) {
        // console.info(`${featureClass} - removeNodes - feature id: ${featureId}`);

        const layerElt = document.getElementById(this.layerId);

        layerElt.querySelectorAll(`g[${this.featureIdAttrName}="${featureId}"]`).forEach(g => g.parentNode.removeChild(g));

        const circle = layerElt.querySelector(`circle[${this.featureIdAttrName}="${featureId}"]`);
        if (circle) {
            circle.parentNode.removeChild(circle);
        }
    }

    private setCircleTransform(featureId: number, pointOffset: any) {
        // console.info(`setCircleTransform - feature id: ${featureId}`);

        const layerElt = document.getElementById(this.layerId);

        if (!layerElt) {
            return;
        }

        const circle: any = layerElt.querySelector(`circle[${this.featureIdAttrName}="${featureId}"]`);
        if (!circle || !circle.e_graphic) {
            return;
        }

        const g: any = layerElt.querySelector(`g[${this.featureIdAttrName}="${featureId}"]`);
        if (!g) {
            return;
        }

        const text = g.querySelector('text');
        if (!text) {
            return;
        }

        const graphic = circle.e_graphic;

        const shape = graphic.getShape().shape;
        const cx = shape.cx;
        const cy = shape.cy;

        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return;
        }

        const circleBBox = circle.getBBox();
        if (circleBBox.width === 0 || circleBBox.height === 0) {
            return;
        }

        const gBBox: any = g.getBBox();
        if (gBBox.width === 0 || gBBox.height === 0) {
            if (!this.setCircleTransformsTimeoutFeatureIds.includes(featureId)) {
                this.setCircleTransformsTimeoutFeatureIds.push(featureId);
            }
            if (this.setCircleTransformsTimeout) {
                clearTimeout(this.setCircleTransformsTimeout);
            }
            // Use native setTimeout method instead of $timeout to avoid triggering angular's digest cycle
            this.setCircleTransformsTimeout = setTimeout(() => this.setCircleTransforms(this.setCircleTransformsTimeoutFeatureIds.slice()), 1000);
            return;
        }

        const featureIdx = this.setCircleTransformsTimeoutFeatureIds.indexOf(featureId);
        if (featureIdx !== -1) {
            this.setCircleTransformsTimeoutFeatureIds.splice(featureIdx, 1);
        }

        let sx = gBBox.width / circleBBox.width;
        let sy = gBBox.height / circleBBox.height;

        if (this.lastScaleFactor && this.lastScaleFactor !== scaleFactor) {
            sx *= scaleFactor / this.lastScaleFactor;
            sy *= scaleFactor / this.lastScaleFactor;
        }

        const rotation = this.layerUtilService.getSvgRotation(graphic.attributes.rotation);
        const tx = cx - (sx * cx + ((text.style.textAnchor === 'start' ? -1 : 0) * sx * circleBBox.width) / 2);
        const ty = cy - sy * cy;

        const transformDefinitions = [];
        if (!this.mapToolsService.isScaleActive() && pointOffset) {
            const translateX = pointOffset.x - cx;
            const translateY = pointOffset.y - cy;
            if (translateX || translateY) {
                transformDefinitions.push(`translate(${translateX}, ${translateY})`);
            }
        }
        transformDefinitions.push(`rotate(${rotation}, ${cx}, ${cy}) matrix(${sx}, 0, 0, ${sy}, ${tx}, ${ty})`);

        circle.setAttribute('transform', transformDefinitions.join(' '));
        circle.setAttribute('pointer-events', 'bounding-box');
    }

    private setCircleTransforms(featureIds: number[]) {
        featureIds.forEach(featureId => this.setCircleTransform(featureId, undefined));
    }
}
